<template>
  <div class="app-layout">
    <el-container>
      <!-- 侧边栏 -->
      <el-aside :width="isCollapse ? '64px' : '200px'" class="sidebar">
        <div class="logo" :class="{ 'logo-collapse': isCollapse }">
          <i class="el-icon-cloudy"></i>
          <span v-if="!isCollapse">青叶智算平台</span>
        </div>
        <el-menu :default-active="activeMenu" :collapse="isCollapse" :collapse-transition="false"
          background-color="#304156" text-color="#bfcbd9" active-text-color="#409eff" router unique-opened>
          <sidebar-item v-for="route in routes" :key="route.path" :item="route" :base-path="route.path" />
        </el-menu>
      </el-aside>

      <!-- 主内容区 -->
      <el-container>
        <!-- 顶部导航栏 -->
        <el-header class="header">
          <div class="header-left">
            <i :class="isCollapse ? 'el-icon-s-unfold' : 'el-icon-s-fold'" class="collapse-icon"
              @click="toggleCollapse"></i>
          </div>
          <div class="header-right">
            <!-- 账户余额 -->
            <div class="balance-info" @click="$router.push('/billing/overview')">
              <i class="el-icon-wallet"></i>
              <span class="balance-label">余额</span>
              <span class="balance-amount">¥{{ accountBalance }}</span>
              <el-button type="text" size="mini" icon="el-icon-refresh" @click.stop="refreshBalance">刷新</el-button>
            </div>

            <!-- 帮助中心 -->
            <el-dropdown @command="handleHelpCommand" class="help-dropdown">
              <span class="nav-item">
                <i class="el-icon-question"></i>
              </span>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="help">
                  <i class="el-icon-document"></i> 帮助中心
                </el-dropdown-item>
                <el-dropdown-item command="contact">
                  <i class="el-icon-phone"></i> 联系我们
                </el-dropdown-item>
                <el-dropdown-item divided command="feedback">
                  <i class="el-icon-edit"></i> 意见反馈
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>

            <!-- 用户信息 -->
            <el-dropdown @command="handleCommand">
              <span class="user-info">
                <el-avatar :size="32" icon="el-icon-user-solid"></el-avatar>
                <span class="username">{{ userInfo.name }}</span>
                <i class="el-icon-arrow-down"></i>
              </span>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="profile">
                  <i class="el-icon-user"></i> 个人中心
                </el-dropdown-item>
                <el-dropdown-item divided command="logout">
                  <i class="el-icon-switch-button"></i> 退出登录
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </el-header>

        <!-- 内容区域 -->
        <el-main class="main-content">
          <transition name="fade-transform" mode="out-in">
            <router-view :key="$route.path" />
          </transition>
        </el-main>
      </el-container>
    </el-container>
  </div>
</template>

<script>
  import { mapState } from 'vuex'
  import { ACCOUNT_BALANCE, initAccountBalanceStorage } from '@/mock/billing'
  import { filterMenuRoutes } from '@/utils/permission'
  import SidebarItem from './SidebarItem.vue'

  export default {
    name: 'Layout',
    components: {
      SidebarItem
    },
    data() {
      return {
        isCollapse: false,
        accountBalance: ACCOUNT_BALANCE
      }
    },
    computed: {
      ...mapState('user', ['userInfo']),
      activeMenu() {
        const route = this.$route
        const { meta, path } = route
        if (meta.activeMenu) {
          return meta.activeMenu
        }
        return path
      },
      routes() {
        const role = this.userInfo.role || 'admin'
        return filterMenuRoutes(this.$router.options.routes, role)
      }
    },
    mounted() {
      initAccountBalanceStorage()
      this.loadAccountBalance()
    },
    methods: {
      toggleCollapse() {
        this.isCollapse = !this.isCollapse
      },
      loadAccountBalance() {
        const balance = localStorage.getItem('accountBalance') || ACCOUNT_BALANCE
        this.accountBalance = balance
      },
      refreshBalance() {
        initAccountBalanceStorage()
        this.loadAccountBalance()
        this.$message.success('余额已更新')
      },
      handleCommand(command) {
        if (command === 'logout') {
          this.$confirm('确定要退出登录吗?', '提示', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }).then(() => {
            this.$store.dispatch('user/logout').then(() => {
              this.$router.push('/login')
              this.$message.success('退出成功')
            })
          })
        } else if (command === 'profile') {
          this.$router.push('/user/profile')
        }
      },
      handleHelpCommand(command) {
        if (command === 'help') {
          this.$router.push('/help/center')
        } else if (command === 'contact') {
          this.$router.push('/help/contact')
        } else if (command === 'feedback') {
          this.$router.push('/help/feedback')
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  .app-layout {
    height: 100%;
  }

  .el-container {
    height: 100%;
  }

  .sidebar {
    background-color: #304156;
    transition: width 0.28s;

    .logo {
      height: 60px;
      line-height: 60px;
      text-align: center;
      font-size: 20px;
      font-weight: bold;
      color: #fff;
      background-color: #2b3a4b;

      i {
        font-size: 28px;
        vertical-align: middle;
      }

      span {
        margin-left: 8px;
        vertical-align: middle;
      }

      &.logo-collapse {
        span {
          display: none;
        }
      }
    }

    .el-menu {
      border-right: none;
    }
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: #fff;
    box-shadow: 0 1px 4px rgba(0, 21, 41, 0.08);

    .header-left {
      .collapse-icon {
        font-size: 20px;
        cursor: pointer;

        &:hover {
          color: #409eff;
        }
      }
    }

    .header-right {
      display: flex;
      align-items: center;
      gap: 20px;

      .balance-info {
        display: flex;
        align-items: center;
        padding: 6px 15px;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        border-radius: 20px;
        cursor: pointer;
        transition: all 0.3s;

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }

        i {
          font-size: 18px;
          color: #fff;
          margin-right: 8px;
        }

        .balance-label {
          font-size: 13px;
          color: rgba(255, 255, 255, 0.9);
          margin-right: 8px;
        }

        .balance-amount {
          font-size: 16px;
          font-weight: bold;
          color: #fff;
          margin-right: 5px;
        }

        ::v-deep .el-button--text {
          color: rgba(255, 255, 255, 0.8);
          padding: 0;

          &:hover {
            color: #fff;
          }
        }
      }

      .help-dropdown {
        .nav-item {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background-color: #f5f7fa;
          cursor: pointer;
          transition: all 0.3s;

          i {
            font-size: 18px;
            color: #606266;
          }

          &:hover {
            background-color: #409eff;

            i {
              color: #fff;
            }
          }
        }
      }

      .user-info {
        display: flex;
        align-items: center;
        cursor: pointer;

        .username {
          margin: 0 8px 0 12px;
        }

        &:hover {
          color: #409eff;
        }
      }
    }
  }

  .main-content {
    background-color: #f0f2f5;
    padding: 20px;
    overflow-y: auto;
  }

  /* 路由切换动画 */
  .fade-transform-leave-active,
  .fade-transform-enter-active {
    transition: all 0.2s;
  }

  .fade-transform-enter {
    opacity: 0;
    transform: translateX(-30px);
  }

  .fade-transform-leave-to {
    opacity: 0;
    transform: translateX(30px);
  }
</style>